import { Box, Button, errorIcon, successIcon, useTheme } from '@ytl/common-web';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as FailedIcon } from '../assets/PageNotFound2.svg';
import { ReactComponent as SuccessIcon } from '../assets/SuccessGirl.svg';
import { selectCurrentUser } from '../contexts/currentUserSlicer';
import { useLanguage } from '../contexts/languageContext';
import { VerificationComponent } from '../modules/verificationComponent/VerificationComponent';
import { AppRoutes } from '../routes';
import { WidgetEvent } from '../shared/@types/widgetEvents';
import { Banned } from '../shared/components/partials/verification/Banned';
import { getConfig } from '../shared/data/config';
import { accountResource } from '../shared/resource/account.resource';
import { onProcessPhase, onWidgetEvent } from '../shared/utils/emitData';
import { createUnsignedJWT } from '../shared/utils/jwtUtils';

enum EmailChangePageState {
  Verification,
  Success,
  Error,
  Banned,
}

enum EmailChangePhase {
  RegisterForm = 'registerForm',
  RegisterWrongEmail = 'registerWrongEmail',
  RegisterAlreadyRegistered = 'registerAlreadyRegistered',
  EmailVerification = 'emailVerification',
  PhoneNumberVerificationCode = 'phoneNumberVerificationCode',
  Finish = 'finish',
  Error = 'error',
  Banned = 'banned',
}

export const EmailChangePage = () => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const [state, setState] = useState<EmailChangePageState>(EmailChangePageState.Verification);
  const currentUser = useSelector(selectCurrentUser);

  // Keep ytl--app class on html element for the entire page lifecycle
  // so the header stays hidden even after VerificationComponent unmounts
  // Re-runs on state change to re-add the class after VerificationComponent's cleanup removes it
  useLayoutEffect(() => {
    if (isWebView) {
      document.documentElement.classList.add('ytl--app');
    }
    return () => {
      document.documentElement.classList.remove('ytl--app');
    };
  }, [isWebView, state]);

  // JWT generálása email verification beállításokkal
  const initParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'email-change-client',
      sessionID: 'email-change-session-' + Date.now(),
      withPassword: false,
      captchaNeeded: true,
      EmailVerification: true,
      TMSVerification: true,
      MSISDNVerification: currentUser?.category !== 'YTV',
      createDUP: false,
      salesMsisdn: '',
      salesCA: '',
      IPBanningEnabled: false,
      language: '',
      theme: '',
      showLoginButton: false,
      additionalInfo: {
        application: isWebView ? 'APP' : 'ALM',
        channel_type: isWebView ? 'mobil_app' : 'web',
        process: 'Kapcsolattartási adatmódosítás',
        sales_type_cd: '7001',
        account_id: currentUser?.accountId || '',
        ca_id: currentUser?.assignedCustomers[0]?.customerAccountId || '',
      },
    };
    return createUnsignedJWT(payload);
  }, [currentUser, isWebView]);

  // As soon as the Success state is reached, invalidate the session immediately.
  useEffect(() => {
    if (state === EmailChangePageState.Success) {
      accountResource.logoutWithConfigUrl(getConfig().openamLogoutUrl);
    }
  }, [state]);

  const [view, setView] = useState<EmailChangePhase>(EmailChangePhase.RegisterForm);
  const [verificationKey, setVerificationKey] = useState(0);

  useEffect(() => {
    const cleanup = onProcessPhase((phase: string) => {
      console.log('Received phase change:', phase);

      switch (phase) {
        case 'GetEmail':
          setView(EmailChangePhase.RegisterForm);
          break;
        case 'EmailConfirm':
          setView(EmailChangePhase.EmailVerification);
          break;
        case 'PhoneNumberConfirm':
          setView(EmailChangePhase.PhoneNumberVerificationCode);
          break;
        case 'Finish':
          //Widget eventre reagálunk
          break;
        case 'Banned':
          setView(EmailChangePhase.Banned);
          break;
      }
    });

    return cleanup;
  }, [currentUser]);

  // Figyeljük a VerificationComponent eseményeit
  useEffect(() => {
    return onWidgetEvent(async (data: any, token: string) => {
      console.log('[EmailChange] WidgetEvent received:', data.eventName, data);
      switch (data.eventName) {
        case WidgetEvent.ProcessFinished:
          if (data.isSuccess) {
            try {
              await accountResource.updateEmailWithValidation(
                data.eventValue.email,
                currentUser?.accountId || '',
                token,
              );
            } catch (err) {
              setState(EmailChangePageState.Error);
              return;
            }
            setView(EmailChangePhase.Finish);
            setState(EmailChangePageState.Success);
          } else {
            setState(EmailChangePageState.Banned);
          }
          break;
      }
    });
  }, [currentUser]);

  const lastHeader = useRef('');
  const header = useMemo(() => {
    let result = '';
    if (view === EmailChangePhase.RegisterForm) {
      result = translation.changeEmail.flow.header.getEmail;
    } else if (view === EmailChangePhase.RegisterWrongEmail) {
      result = translation.changeEmail.flow.header.getEmail;
    } else if (view === EmailChangePhase.RegisterAlreadyRegistered) {
      result = translation.changeEmail.flow.header.getEmail;
    } else if (view === EmailChangePhase.EmailVerification) {
      result = isWebView
        ? translation.changeEmail.flow.header.confirmEmailApp
        : translation.changeEmail.flow.header.confirmEmail;
    } else if (view === EmailChangePhase.PhoneNumberVerificationCode) {
      result = translation.changeEmail.flow.header.confirmPhone;
    } else if (view === EmailChangePhase.Error) {
      result = '';
    }
    if (result) lastHeader.current = result;
    return lastHeader.current;
  }, [view, translation, isWebView]);

  //TODO: ha nem kell useMemo lehet egyszerűsíteni
  const lastText = useRef('');
  const text = useMemo(() => {
    let result = '';
    if (view === EmailChangePhase.RegisterForm) {
      result = translation.changeEmail.flow.text.getEmail;
    } else if (view === EmailChangePhase.RegisterWrongEmail) {
      result = translation.changeEmail.flow.text.getEmail;
    } else if (view === EmailChangePhase.RegisterAlreadyRegistered) {
      result = translation.changeEmail.flow.text.getEmail;
    } else if (view === EmailChangePhase.EmailVerification) {
      result = translation.changeEmail.flow.text.confirmEmail;
    } else if (view === EmailChangePhase.PhoneNumberVerificationCode) {
      result = translation.changeEmail.flow.text.confirmPhone;
    } else if (view === EmailChangePhase.Error) {
      result = '';
    }
    if (result) lastText.current = result;
    return lastText.current;
  }, [view, translation]);

  const resetVerification = () => {
    setVerificationKey(k => k + 1);
  };

  const successBoxConfig = useMemo(
    () => ({
      ...successIcon,
      headerHTML: translation.changeEmail.flow.header.success,
      subHeader: translation.changeEmail.flow.text.success,
      action: (
        <Button
          onClick={() =>
            isWebView
              ? (window.location.href = getConfig().appLogoutUrl)
              : (window.location.href = getConfig().mainYettelUrl)
          }
        >
          {translation.changeEmail.goToLogin}
        </Button>
      ),
    }),
    [translation, isWebView],
  );

  const errorBoxConfig = useMemo(
    () => ({
      ...errorIcon,
      headerHTML: translation.changeEmail.flow.header.failed,
      subHeader: translation.changeEmail.flow.text.failed,
      action: <Button onClick={resetVerification}>{translation.changeEmail.retry}</Button>,
    }),
    [translation],
  );

  const backButtonAction = () => {
    if (view !== EmailChangePhase.RegisterForm) {
      resetVerification();
    } else {
      if (isWebView) {
        window.location.href = getConfig().closeWebViewUrl;
      } else {
        AppRoutes.goToYettelAccount();
      }
    }
  };

  const BackButton = () => {
    return (
      <Button
        variant="text-undecorated"
        onClick={backButtonAction}
        /* onClick={() =>
          isWebView ? (window.location.href = getConfig().closeWebViewUrl) : AppRoutes.goToYettelAccount()
        } */
      >
        {translation.changePhone.buttonTextBack}
      </Button>
    );
  };

  const exitButtonAction = () => {
    if (isWebView) {
      window.location.href = getConfig().closeWebViewUrl;
    } else {
      AppRoutes.goToYettelAccount();
    }
  };

  return (
    <>
      {state === EmailChangePageState.Banned ? (
        <Banned buttonAction={exitButtonAction} />
      ) : (
        <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
          {state === EmailChangePageState.Success && (
            <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
              {isWebView ? (
                <div className="account-flow__finish">
                  <SuccessIcon />
                  <h3>{translation.changeEmail.flow.header.success}</h3>
                  <p dangerouslySetInnerHTML={{ __html: translation.changeEmail.flow.text.successApp }}></p>
                  <Button
                    variant="contained"
                    onClick={() => (window.location.href = getConfig().appLogoutUrl)}
                  >
                    {translation.changePassword.continueToAccount}
                  </Button>
                </div>
              ) : (
                <Box {...successBoxConfig} />
              )}
            </div>
          )}

          {state === EmailChangePageState.Error && (
            <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
              {isWebView ? (
                <div className="account-flow__finish">
                  <FailedIcon />
                  <h3>{translation.changeEmail.flow.header.failed}</h3>
                  <p dangerouslySetInnerHTML={{ __html: translation.changeEmail.flow.text.failedApp }}></p>
                  <Button
                    variant="contained"
                    onClick={() => (window.location.href = getConfig().appRedirectBackUrl)}
                  >
                    {translation.changePassword.backToEdit}
                  </Button>
                </div>
              ) : (
                <Box {...errorBoxConfig} />
              )}
            </div>
          )}

          {state === EmailChangePageState.Verification && currentUser && (
            <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
              <h3>{header}</h3>
              <p dangerouslySetInnerHTML={{ __html: text }}></p>
              <VerificationComponent
                key={verificationKey}
                initParamsJWT={initParamsJWT}
                emailAgain
                showFinishScreen={false}
                skipGetPhoneNumber
                initialPhoneNumber={[currentUser?.ctn || '']}
                disableBackButton
              />
              <BackButton />
            </div>
          )}
        </div>
      )}
    </>
  );
};
